import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  Popover,
  Typography,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Avatar,
  useMediaQuery,
  useTheme,
  Stack,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility,
  VisibilityOff,
  Refresh,
  Print,
  Download,
  Business,
  Phone,
  LocationOn,
  Receipt,
  Badge,
} from "@mui/icons-material";
import {
  partyList,
  deleteParty,
  updateParty,
} from "../../../Services/Services";
import AddParty from "./AddParty";
import { T } from "../../../theme";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ── Reusable Style Fragments ───────────────────────────────────────────────────
const sq = { borderRadius: "4px !important" };

const inputSx = {
  "& .MuiOutlinedInput-root": {
    ...sq,
    bgcolor: T.white,
    transition: "box-shadow 0.2s",
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
    "&.Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${T.primaryLight}` },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const getInitials = (name) =>
  name
    ?.split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) ?? "?";

// ── Shared Button Variants ─────────────────────────────────────────────────────
const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.white,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};

const iconBtn = { ...ghostBtn, minWidth: 0, px: 1 };

const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};

const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};

const paginationSx = {
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  mt: 1,
  ...sq,
  "& .MuiTablePagination-toolbar": {
    minHeight: 44,
    flexWrap: "wrap",
    gap: 0.5,
    px: 1,
  },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
    fontSize: "0.68rem",
    margin: 0,
    color: T.textSub,
    fontWeight: 500,
  },
  "& .MuiTablePagination-select": {
    fontSize: "0.68rem",
    ...sq,
    border: `1px solid ${T.border}`,
    bgcolor: T.white,
    px: "6px",
  },
  "& .MuiTablePagination-actions button": { color: T.primary },
  "& .MuiTablePagination-actions button:disabled": { color: T.textMuted },
};

// ── ActionButton ───────────────────────────────────────────────────────────────
const ActionButton = ({
  tip,
  icon,
  isDelete = false,
  isEdit = false,
  isView = false,
  onClick,
}) => {
  const color = isDelete
    ? "#dc2626"
    : isEdit
      ? "#757575"
      : isView
        ? "#1976d2"
        : T.primary;
  const bg = isDelete
    ? T.redBg
    : isEdit
      ? "#f5f5f5"
      : isView
        ? "#e3f2fd"
        : T.primaryBg;
  const border = isDelete
    ? T.redBorder
    : isEdit
      ? "#e0e0e0"
      : isView
        ? "#bbdefb"
        : T.border;
  const hoverBg = isDelete
    ? "#fee2e2"
    : isEdit
      ? "#eeeeee"
      : isView
        ? "#bbdefb"
        : T.primaryLight;

  return (
    <Tooltip title={tip} arrow placement="top">
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 20,
          height: 20,
          ...sq,
          color,
          bgcolor: bg,
          border: `1px solid ${border}`,
          transition: "all 0.18s",
          "&:hover": {
            bgcolor: hoverBg,
            borderColor: color,
            transform: "translateY(-1px)",
          },
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 12 } })}
      </IconButton>
    </Tooltip>
  );
};

// ── InfoRow ────────────────────────────────────────────────────────────────────
const InfoRow = ({ Icon, label, val }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
    <Box
      sx={{
        width: 24,
        height: 24,
        ...sq,
        bgcolor: T.white,
        border: `1px solid ${T.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 13, color: T.primary }} />
    </Box>
    <Box>
      <Typography
        sx={{
          fontSize: "0.62rem",
          color: T.textMuted,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.75rem",
          color: T.text,
          fontWeight: 500,
          lineHeight: 1.3,
        }}
      >
        {val || "—"}
      </Typography>
    </Box>
  </Box>
);

// ── DialogHeader ───────────────────────────────────────────────────────────────
const DialogHeader = ({ title, onClose }) => (
  <Box
    sx={{
      background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryMid} 100%)`,
      px: 2.5,
      py: 1.75,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Typography
      sx={{
        fontWeight: 700,
        color: T.white,
        fontSize: "0.88rem",
        letterSpacing: "0.01em",
      }}
    >
      {title}
    </Typography>
    <IconButton
      onClick={onClose}
      size="small"
      sx={{
        color: "rgba(255,255,255,0.8)",
        p: 0.5,
        ...sq,
        "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: T.white },
      }}
    >
      <CloseIcon sx={{ fontSize: 17 }} />
    </IconButton>
  </Box>
);

// ── MobilePartyCard ────────────────────────────────────────────────────────────
const MobilePartyCard = ({ party, expanded, onToggle, onEdit, onDelete }) => (
  <Paper
    elevation={0}
    sx={{
      mb: 1,
      ...sq,
      border: `1.5px solid ${expanded ? T.primary : T.border}`,
      bgcolor: T.white,
      overflow: "hidden",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: expanded ? `0 2px 12px rgba(79,70,229,0.1)` : "none",
    }}
  >
    {/* Top Row */}
    <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
      {/* Avatar */}
      <Box
        sx={{
          width: 60,
          bgcolor: expanded ? T.primary : T.primaryBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
          transition: "background-color 0.2s",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: "1.1rem",
            fontWeight: 800,
            color: expanded ? T.white : T.primary,
            lineHeight: 1,
          }}
        >
          {getInitials(party.name)}
        </Typography>
      </Box>

      {/* Name + ID */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          px: 1.5,
          py: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.82rem",
            color: T.text,
            lineHeight: 1.2,
            mb: 0.3,
          }}
        >
          {party.name || "—"}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.65rem",
            color: T.textMuted,
            fontWeight: 600,
            letterSpacing: "0.03em",
          }}
        >
          ID: {party.party_id}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mt: 0.4 }}>
          <Phone sx={{ fontSize: 11, color: T.primary }} />
          <Typography
            sx={{
              fontSize: "0.68rem",
              color: T.text,
              fontWeight: 600,
              fontFamily: "monospace",
            }}
          >
            {party.contact_no || "N/A"}
          </Typography>
        </Box>
      </Box>

      {/* Toggle */}
      <Box sx={{ display: "flex", alignItems: "center", pr: 1.5 }}>
        <Button
          size="small"
          onClick={onToggle}
          startIcon={
            expanded ? (
              <VisibilityOff sx={{ fontSize: 12 }} />
            ) : (
              <Visibility sx={{ fontSize: 12 }} />
            )
          }
          sx={{
            ...ghostBtn,
            height: 28,
            fontSize: "0.62rem",
            px: 1,
            "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
          }}
        >
          {expanded ? "Hide" : "Details"}
        </Button>
      </Box>
    </Box>

    {/* Expanded Panel */}
    {expanded && (
      <Box
        sx={{
          bgcolor: T.white,
          borderTop: `1.5px solid ${T.border}`,
          p: 1.5,
        }}
      >
        <Stack spacing={1}>
          <InfoRow Icon={Phone} label="Contact" val={party.contact_no} />
          {party.contact_no1 && (
            <InfoRow Icon={Phone} label="Alternate 1" val={party.contact_no1} />
          )}
          {party.contact_no2 && (
            <InfoRow Icon={Phone} label="Alternate 2" val={party.contact_no2} />
          )}
          <InfoRow Icon={LocationOn} label="Address" val={party.address} />
          {party.gst_no && (
            <InfoRow Icon={Receipt} label="GST No" val={party.gst_no} />
          )}
          {party.pan_no && (
            <InfoRow Icon={Badge} label="PAN No" val={party.pan_no} />
          )}
          {party.tds_percent > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  ...sq,
                  bgcolor: "#FEF2F2",
                  border: "1px solid #FECACA",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{ fontSize: "0.6rem", fontWeight: 800, color: "#DC2626" }}
                >
                  %
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.62rem",
                    color: T.textMuted,
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  TDS Rate
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "#DC2626",
                    fontWeight: 700,
                  }}
                >
                  {party.tds_percent}% deduction
                </Typography>
              </Box>
            </Box>
          )}
        </Stack>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            mt: 1.5,
            pt: 1.25,
            borderTop: `1px dashed ${T.border}`,
            justifyContent: "flex-end",
          }}
        >
          <Button
            size="small"
            startIcon={<EditIcon sx={{ fontSize: 13 }} />}
            onClick={() => onEdit(party)}
            sx={{ ...ghostBtn, height: 30, px: 1.5 }}
          >
            Edit
          </Button>
          <Button
            size="small"
            startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}
            onClick={(e) => onDelete(e, party.party_id)}
            sx={{ ...dangerBtn, height: 30, px: 1.5 }}
          >
            Delete
          </Button>
        </Box>
      </Box>
    )}
  </Paper>
);

// ── DesktopPartyTable ──────────────────────────────────────────────────────────
const DesktopPartyTable = ({
  parties,
  loading,
  totalCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onView,
  onEdit,
  onDelete,
}) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 10,
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: T.primary }} size={36} />
        <Typography
          sx={{ fontSize: "0.75rem", color: T.textSub, fontWeight: 500 }}
        >
          Loading parties...
        </Typography>
      </Box>
    );
  }

  const columns = [
    { label: "#", align: "center", sticky: true, left: 0, minW: 48 },
    { label: "Consignor", align: "left", sticky: true, left: 48, minW: 180 },
    { label: "Contact", align: "center", minW: 120 },
    { label: "Alternate", align: "center", minW: 140 },
    { label: "Address", align: "center", minW: 200 },
    { label: "GST No", align: "center", minW: 160 },
    { label: "PAN No", align: "center", minW: 120 },
    { label: "TDS %", align: "center", minW: 70 },
    { label: "Actions", align: "center", sticky: true, right: 0, minW: 110 },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        ...sq,
        border: `1.5px solid ${T.border}`,
        overflow: "hidden",
        boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
      }}
    >
      <TableContainer
        sx={{ maxHeight: "calc(100vh - 280px)", overflow: "auto" }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            minWidth: 1100,
            borderCollapse: "collapse",
            "& td, & th": {
              py: 0,
              px: 1,
              fontSize: "0.75rem",
              borderBottom: `1px solid #ccc`,
              lineHeight: 1.2,
            },
            "& th": {
              fontWeight: 700,
              color: T.headerText,
              bgcolor: `${T.headerBg} !important`,
              py: 0.2,
              fontSize: "0.8rem",
              letterSpacing: "0.01em",
              borderBottom: `1px solid #ccc`,
              lineHeight: 1.2,
              height: 28,
            },
            "& td": {
              height: 26,
            },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map(({ label, align, sticky, left, right, minW }) => (
                <TableCell
                  key={label}
                  align={align}
                  sx={{
                    minWidth: minW,
                    ...(sticky && {
                      position: "sticky",
                      ...(left !== undefined && { left }),
                      ...(right !== undefined && { right }),
                      zIndex: 12,
                      bgcolor: `${T.headerBg} !important`,
                      color: `${T.headerText} !important`,
                    }),
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {parties.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  sx={{ textAlign: "center", py: 6, border: "none" }}
                >
                  <Business
                    sx={{
                      fontSize: 40,
                      color: T.border,
                      mb: 1,
                      display: "block",
                      mx: "auto",
                    }}
                  />
                  <Typography
                    sx={{
                      color: T.textSub,
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    }}
                  >
                    No parties found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              parties.map((party, idx) => {
                const isEven = idx % 2 === 0;
                const rowBg = isEven ? T.baseRow : T.altRow;

                return (
                  <TableRow
                    key={party.party_id}
                    sx={{
                      "& > td": {
                        bgcolor: rowBg,
                        transition: "background-color 0.12s",
                      },
                      "&:hover > td": {
                        bgcolor: `${T.rowHover} !important`,
                      },
                    }}
                  >
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        left: 0,
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: T.textMuted,
                        }}
                      >
                        {page * rowsPerPage + idx + 1}
                      </Typography>
                    </TableCell>

                    {/* Consignor Name */}
                    <TableCell
                      align="left"
                      sx={{
                        position: "sticky",
                        left: 48,
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                        py: "2px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                          sx={{
                            width: 20,
                            height: 20,
                            ...sq,
                            bgcolor: T.primaryLight,
                            color: T.primary,
                            fontSize: "0.55rem",
                            fontWeight: 800,
                            border: `1px solid ${T.border}`,
                            flexShrink: 0,
                          }}
                        >
                          {getInitials(party.name)}
                        </Avatar>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: T.text,
                          }}
                        >
                          {party.name || "—"}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* Contact */}
                    <TableCell align="center">
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: T.text,
                          fontWeight: 600,
                          fontFamily: "monospace",
                        }}
                      >
                        {party.contact_no || "—"}
                      </Typography>
                    </TableCell>

                    {/* Alternate */}
                    <TableCell align="center">
                      <Stack spacing={0.25}>
                        {party.contact_no1 && (
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: T.text,
                              fontWeight: 500,
                              fontFamily: "monospace",
                            }}
                          >
                            {party.contact_no1}
                          </Typography>
                        )}
                        {party.contact_no2 && (
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: T.text,
                              fontWeight: 500,
                              fontFamily: "monospace",
                            }}
                          >
                            {party.contact_no2}
                          </Typography>
                        )}
                        {!party.contact_no1 && !party.contact_no2 && (
                          <Typography
                            sx={{ fontSize: "0.75rem", color: T.textMuted }}
                          >
                            —
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>

                    {/* Address */}
                    <TableCell align="center">
                      <Tooltip title={party.address || ""} arrow>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: T.text,
                            fontWeight: 500,
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {party.address || "—"}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* GST No */}
                    <TableCell align="center">
                      {party.gst_no ? (
                        <Chip
                          label={party.gst_no}
                          size="small"
                          sx={{
                            fontSize: "0.6rem",
                            height: 16,
                            ...sq,
                            bgcolor: "#ede9fe",
                            color: "#5b21b6",
                            border: "1px solid #ddd6fe",
                            fontWeight: 700,
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      ) : (
                        <Typography
                          sx={{ fontSize: "0.75rem", color: T.textMuted }}
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>

                    {/* PAN No */}
                    <TableCell align="center">
                      {party.pan_no ? (
                        <Chip
                          label={party.pan_no}
                          size="small"
                          sx={{
                            fontSize: "0.6rem",
                            height: 16,
                            ...sq,
                            bgcolor: "#fef3c7",
                            color: "#92400e",
                            border: "1px solid #fde68a",
                            fontWeight: 700,
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      ) : (
                        <Typography
                          sx={{ fontSize: "0.75rem", color: T.textMuted }}
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      {party.tds_percent > 0 ? (
                        <Chip
                          label={`${party.tds_percent}%`}
                          size="small"
                          sx={{
                            fontSize: "0.6rem",
                            height: 16,
                            ...sq,
                            bgcolor: "#FEE2E2",
                            color: "#DC2626",
                            border: "1px solid #FECACA",
                            fontWeight: 700,
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      ) : (
                        <Typography
                          sx={{ fontSize: "0.75rem", color: T.textMuted }}
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        right: 0,
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                        py: "2px !important",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                      >
                        <ActionButton
                          tip="View"
                          icon={<Visibility />}
                          isView
                          onClick={() => onView(party)}
                        />
                        <ActionButton
                          tip="Edit"
                          icon={<EditIcon />}
                          isEdit
                          onClick={() => onEdit(party)}
                        />
                        <ActionButton
                          tip="Delete"
                          icon={<DeleteIcon />}
                          isDelete
                          onClick={(e) => onDelete(e, party.party_id)}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalCount > 0 && (
        <Box sx={{ borderTop: `1px solid #ccc`, bgcolor: T.white }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            sx={{
              "& .MuiTablePagination-toolbar": { minHeight: 36, px: 2 },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  fontSize: "0.7rem",
                  color: T.textSub,
                  fontWeight: 500,
                  margin: 0,
                },
              "& .MuiTablePagination-select": {
                fontSize: "0.7rem",
                ...sq,
                border: `1px solid #ccc`,
                bgcolor: T.white,
                px: "5px",
              },
              "& .MuiTablePagination-actions button": { color: T.primary },
              "& .MuiTablePagination-actions button:disabled": {
                color: T.textMuted,
              },
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

// ── ViewPartyDialog ────────────────────────────────────────────────────────────
const ViewPartyDialog = ({ open, onClose, party }) => {
  if (!party) return null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          ...sq,
          border: `1.5px solid ${T.border}`,
          boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
          overflow: "hidden",
        },
      }}
    >
      <DialogHeader title="Consignor Details" onClose={onClose} />

      <DialogContent sx={{ p: 2.5, bgcolor: T.white }}>
        <Stack alignItems="center" sx={{ mb: 2.5 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              ...sq,
              background: `linear-gradient(135deg, ${T.primaryLight}, ${T.primaryBg})`,
              color: T.primary,
              fontSize: "1.5rem",
              fontWeight: 800,
              border: `2px solid ${T.border}`,
              mb: 1,
            }}
          >
            {getInitials(party.name)}
          </Avatar>
          <Typography sx={{ fontWeight: 800, color: T.text, fontSize: "1rem" }}>
            {party.name}
          </Typography>
          <Chip
            label={`ID: ${party.party_id}`}
            size="small"
            sx={{
              mt: 0.5,
              ...sq,
              bgcolor: T.white,
              color: T.primary,
              border: `1px solid ${T.border}`,
              fontSize: "0.62rem",
              height: 20,
              fontWeight: 700,
            }}
          />
        </Stack>

        <Box
          sx={{
            p: 2,
            bgcolor: T.white,
            border: `1.5px solid ${T.border}`,
            ...sq,
            mb: 1.5,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: T.primary,
              mb: 1.25,
              fontSize: "0.68rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            Contact Information
          </Typography>
          <Stack spacing={1.25}>
            <InfoRow Icon={Phone} label="Contact" val={party.contact_no} />
            {party.contact_no1 && (
              <InfoRow
                Icon={Phone}
                label="Alternate 1"
                val={party.contact_no1}
              />
            )}
            {party.contact_no2 && (
              <InfoRow
                Icon={Phone}
                label="Alternate 2"
                val={party.contact_no2}
              />
            )}
            <InfoRow Icon={LocationOn} label="Address" val={party.address} />
          </Stack>
        </Box>

        {(party.gst_no || party.pan_no || party.tds_percent > 0) && (
          <Box
            sx={{
              p: 2,
              bgcolor: T.white,
              border: `1.5px solid ${T.border}`,
              ...sq,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                color: T.primary,
                mb: 1.25,
                fontSize: "0.68rem",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              Tax Information
            </Typography>
            <Stack spacing={1.25}>
              {party.gst_no && (
                <InfoRow Icon={Receipt} label="GST No" val={party.gst_no} />
              )}
              {party.pan_no && (
                <InfoRow Icon={Badge} label="PAN No" val={party.pan_no} />
              )}
              {/* TDS % */}
              {party.tds_percent > 0 && (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      ...sq,
                      bgcolor: "#FEF2F2",
                      border: "1px solid #FECACA",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.6rem",
                        fontWeight: 800,
                        color: "#DC2626",
                      }}
                    >
                      %
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.62rem",
                        color: T.textMuted,
                        fontWeight: 600,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                      }}
                    >
                      TDS Rate
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "#DC2626",
                        fontWeight: 700,
                      }}
                    >
                      {party.tds_percent}% (deducted from bill)
                    </Typography>
                  </Box>
                </Box>
              )}
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: T.white,
          borderTop: `1px solid ${T.border}`,
        }}
      >
        <Button
          onClick={onClose}
          size="small"
          sx={{ ...ghostBtn, height: 32, px: 2 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── DeletePopover ──────────────────────────────────────────────────────────────
const DeletePopover = ({ anchorEl, onClose, onConfirm }) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    transformOrigin={{ vertical: "top", horizontal: "center" }}
    PaperProps={{
      sx: {
        ...sq,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        border: `1.5px solid ${T.border}`,
        bgcolor: T.white,
      },
    }}
  >
    <Box sx={{ p: 2, maxWidth: 240 }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            ...sq,
            bgcolor: T.redBg,
            border: `1.5px solid ${T.redBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DeleteIcon sx={{ fontSize: 20, color: T.red }} />
        </Box>
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.78rem",
          color: T.text,
          textAlign: "center",
          mb: 0.5,
        }}
      >
        Delete Consignor?
      </Typography>
      <Typography
        sx={{
          fontSize: "0.67rem",
          color: T.textSub,
          fontWeight: 500,
          textAlign: "center",
          mb: 1.75,
        }}
      >
        This action cannot be undone.
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75 }}>
        <Button
          fullWidth
          size="small"
          onClick={onClose}
          sx={{ ...ghostBtn, height: 30, fontSize: "0.7rem" }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          size="small"
          onClick={onConfirm}
          sx={{ ...dangerBtn, height: 30, fontSize: "0.7rem" }}
        >
          Delete
        </Button>
      </Box>
    </Box>
  </Popover>
);

// ── Main Component ─────────────────────────────────────────────────────────────
export default function PartyList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [parties, setParties] = useState([]);
  const [filteredParties, setFilteredParties] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedParty, setSelectedParty] = useState(null);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [partyToDelete, setPartyToDelete] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editPartyData, setEditPartyData] = useState({});
  const [editPartyId, setEditPartyId] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [editDuplicateOpen, setEditDuplicateOpen] = useState(false);

  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));

  const fetchParties = useCallback(async () => {
    setLoading(true);
    try {
      const res = await partyList({ party_type: "consignor" });
      setParties(res || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load parties");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchParties();
  }, [fetchParties]);

  useEffect(() => {
    const q = searchTerm.toLowerCase();
    const filtered = parties.filter(
      (p) =>
        !q ||
        p.name?.toLowerCase().includes(q) ||
        p.address?.toLowerCase().includes(q) ||
        p.contact_no?.includes(q),
    );
    setFilteredParties(filtered);
    setTotalCount(filtered.length);
    setPage(0);
    setExpandedCardId(null);
  }, [parties, searchTerm]);

  const handleView = (party) => {
    setSelectedParty(party);
    setViewOpen(true);
  };

  const handleEdit = (party) => {
    setEditPartyData({ ...party });
    setEditPartyId(party.party_id);
    setEditModalOpen(true);
  };

  const handleEditChange = ({ target: { name, value } }) => {
    const updatedErrors = { ...editErrors };

    if (["contact_no", "contact_no1", "contact_no2"].includes(name)) {
      const digits = value.replace(/\D/g, "").slice(0, 10);
      setEditPartyData((p) => ({ ...p, [name]: digits }));
      // Only validate when done typing (10 digits or empty)
      if (digits.length > 0 && digits.length < 10) {
        updatedErrors[name] = "Must be exactly 10 digits";
      } else {
        delete updatedErrors[name];
      }
    } else if (name === "gst_no") {
      const upper = value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 15);
      setEditPartyData((p) => ({ ...p, [name]: upper }));
      // Validate only when 15 chars entered
      if (upper.length > 0 && upper.length === 15) {
        if (
          !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
            upper,
          )
        ) {
          updatedErrors[name] = "Invalid GST (e.g. 22AAAAA0000A1Z5)";
        } else {
          delete updatedErrors[name];
        }
      } else if (upper.length > 0 && upper.length < 15) {
        updatedErrors[name] = `GST must be 15 characters (${upper.length}/15)`;
      } else {
        delete updatedErrors[name];
      }
    } else if (name === "pan_no") {
      const upper = value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 10);
      setEditPartyData((p) => ({ ...p, [name]: upper }));
      // Validate only when 10 chars entered
      if (upper.length > 0 && upper.length === 10) {
        if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(upper)) {
          updatedErrors[name] = "Invalid PAN (e.g. ABCDE1234F)";
        } else {
          delete updatedErrors[name];
        }
      } else if (upper.length > 0 && upper.length < 10) {
        updatedErrors[name] = `PAN must be 10 characters (${upper.length}/10)`;
      } else {
        delete updatedErrors[name];
      }
    } else {
      setEditPartyData((p) => ({ ...p, [name]: value }));
      delete updatedErrors[name];
    }

    setEditErrors(updatedErrors);
  };

  const handleSaveEdit = async (force = false) => {
    const errs = {};

    if (
      editPartyData.contact_no &&
      !/^\d{10}$/.test(editPartyData.contact_no)
    ) {
      errs.contact_no = "Must be exactly 10 digits";
    }

    if (
      editPartyData.gst_no &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        editPartyData.gst_no,
      )
    ) {
      errs.gst_no = "Invalid GST format";
    }

    if (
      editPartyData.pan_no &&
      !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(editPartyData.pan_no)
    ) {
      errs.pan_no = "Invalid PAN format";
    }

    if (Object.keys(errs).length) {
      setEditErrors(errs);
      return;
    }

    try {
      const res = await updateParty(editPartyId, {
        ...editPartyData,
        party_type: "consignor",
        force,
      });

      if (res?.duplicate && !force) {
        setEditDuplicateOpen(true);
        return;
      }

      setParties(res?.parties || []);
      setEditModalOpen(false);
      setEditErrors({});
      setEditDuplicateOpen(false);
      toast.success("Consignor updated successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update consignor");
    }
  };
  const handleDeleteClick = (e, id) => {
    setDeleteAnchorEl(e.currentTarget);
    setPartyToDelete(id);
  };
  const handleDeleteClose = () => {
    setDeleteAnchorEl(null);
    setPartyToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!partyToDelete) return;
    try {
      const res = await deleteParty(partyToDelete);
      setParties(res?.parties || []);
      toast.success("Consignor deleted successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete consignor");
    }
    handleDeleteClose();
  };

  const handlePrint = () => {
    const w = window.open("", "_blank");
    w.document.write(`
      <html><head><title>Consignor List</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family:'Segoe UI',Arial,sans-serif; padding:30px; color:#1e1b4b; }
        h2 { text-align:center; font-size:18px; margin-bottom:20px; color:#4f46e5; }
        table { width:100%; border-collapse:collapse; font-size:12px; }
        th { background:#4f46e5; color:white; padding:8px 10px; text-align:left; font-weight:600; }
        td { padding:7px 10px; border-bottom:1px solid #e0e7ff; }
        tr:nth-child(even) td { background:#f5f3ff; }
      </style></head>
      <body><h2>Consignor List</h2>
      <table><thead><tr><th>#</th><th>Consignor Name</th><th>Contact</th><th>Alternate</th><th>Address</th><th>PAN No</th><th>GST No</th><th>TDS %</th></tr></thead>
      <tbody>${filteredParties
        .map(
          (p, i) => `
        <tr><td>${i + 1}</td><td>${p.name || "—"}</td><td>${p.contact_no || "—"}</td>
        <td>${[p.contact_no1, p.contact_no2].filter(Boolean).join(", ") || "—"}</td>
        <td>${p.address || "—"}</td><td>${p.gst_no || "—"}</td><td>${p.pan_no || "—"}</td> <td style="color:#DC2626;font-weight:700">
  ${p.tds_percent > 0 ? p.tds_percent + "%" : "—"}
</td></tr>
      `,
        )
        .join("")}</tbody></table></body></html>
    `);
    w.document.close();
    w.print();
    w.close();
  };

  const handleExportExcel = async () => {
    const XLSX = await import("xlsx-js-style");

    const headers = [
      "Sr No",
      "Consignor Name",
      "Contact",
      "Alternate",
      "Address",
      "GST No",
      "PAN No",
      "TDS %",
    ];

    const rows = filteredParties.map((p, i) => [
      i + 1,
      p.name || "—",
      p.contact_no || "—",
      p.contact_no1 || "—",
      p.address || "—",
      p.gst_no || "—",
      p.pan_no || "—",
      p.tds_percent > 0 ? `${p.tds_percent}%` : "—",
    ]);

    // ── Style Definitions ──────────────────────────────────────────
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10, name: "Segoe UI" },
      fill: { fgColor: { rgb: "4F46E5" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "3730A3" } },
        bottom: { style: "thin", color: { rgb: "3730A3" } },
        left: { style: "thin", color: { rgb: "3730A3" } },
        right: { style: "thin", color: { rgb: "3730A3" } },
      },
    };

    const cellStyleEven = {
      font: { sz: 10, name: "Segoe UI", color: { rgb: "1E1B4B" } },
      fill: { fgColor: { rgb: "F5F3FF" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "E0E7FF" } },
        bottom: { style: "thin", color: { rgb: "E0E7FF" } },
        left: { style: "thin", color: { rgb: "E0E7FF" } },
        right: { style: "thin", color: { rgb: "E0E7FF" } },
      },
    };

    const cellStyleOdd = {
      ...cellStyleEven,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    const numberStyle = {
      ...cellStyleEven,
      font: { ...cellStyleEven.font, bold: true, color: { rgb: "4F46E5" } },
    };

    const numberStyleOdd = {
      ...numberStyle,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    // ── Build Worksheet Data ───────────────────────────────────────
    const wsData = [
      headers.map((h) => ({ v: h, t: "s", s: headerStyle })),
      ...rows.map((row, rowIdx) =>
        row.map((val, colIdx) => {
          const isEven = rowIdx % 2 === 0;
          const isNumber = colIdx === 0;

          let style;
          if (isNumber) {
            style = isEven ? numberStyle : numberStyleOdd;
          } else {
            style = isEven ? cellStyleEven : cellStyleOdd;
          }

          return {
            v: val ?? "—",
            t: typeof val === "number" ? "n" : "s",
            s: style,
          };
        }),
      ),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // ── Column Widths (no wrapping, width based on longest content) ──
    const colWidths = headers.map((_, colIdx) => {
      const allValues = [
        headers[colIdx],
        ...rows.map((r) => String(r[colIdx] ?? "")),
      ];
      const maxLen = Math.max(...allValues.map((v) => v.length));

      return {
        wch:
          colIdx === 4
            ? maxLen + 6 // Address column extra width
            : maxLen + 4,
      };
    });

    ws["!cols"] = colWidths;

    // ── Row Heights ────────────────────────────────────────────────
    ws["!rows"] = [{ hpt: 18 }];

    // ── Freeze Header Row ──────────────────────────────────────────
    ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2" };

    const wb = XLSX.utils.book_new();
    wb.Props = {
      Title: "Consignor List",
      Author: "Paras Roadlines",
      CreatedDate: new Date(),
    };

    XLSX.utils.book_append_sheet(wb, ws, "Parties");
    XLSX.writeFile(
      wb,
      `Party_List_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  const paginatedParties = filteredParties.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        style={{ top: "70px", right: "16px", zIndex: 99999 }}
      />

      <Box sx={{ px: 0, pt: 0, mt: -5, maxWidth: 1400, mx: "auto" }}>
        {/* ── Toolbar ── */}
        <Box sx={{ mt: 2, mb: 1.5 }}>
          <Grid container spacing={0.75} alignItems="center">
            {/* Search */}
            <Grid item xs={12} sm={4} md={3.5}>
              <TextField
                fullWidth
                placeholder="Search by name, contact or address…"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 15, color: T.primary }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm("")}
                        sx={{ p: 0.25 }}
                      >
                        <CloseIcon sx={{ fontSize: 13, color: T.textSub }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    height: 34,
                    ...sq,
                    bgcolor: T.white,
                    transition: "box-shadow 0.2s",
                  },
                  "& fieldset": { border: `1.5px solid ${T.border}` },
                  "& :hover fieldset": {
                    border: `1.5px solid ${T.borderHover}`,
                  },
                  "& .Mui-focused fieldset": {
                    border: `1.5px solid ${T.primary}`,
                  },
                  "& .Mui-focused": {
                    boxShadow: `0 0 0 3px ${T.primaryLight}`,
                  },
                }}
              />
            </Grid>

            {/* Reset */}
            <Grid item xs={4} sm={1.5} md={1}>
              <Button
                fullWidth
                size="small"
                onClick={() => setSearchTerm("")}
                sx={{ ...ghostBtn, height: 34 }}
              >
                Reset
              </Button>
            </Grid>

            {/* Icon Actions */}
            {[
              {
                tip: "Print",
                icon: <Print sx={{ fontSize: 15 }} />,
                action: handlePrint,
              },
              {
                tip: "Export Excel",
                icon: <Download sx={{ fontSize: 15 }} />,
                action: handleExportExcel,
              },
              {
                tip: "Refresh",
                icon: <Refresh sx={{ fontSize: 15 }} />,
                action: fetchParties,
              },
            ].map(({ tip, icon, action }) => (
              <Grid item xs={2} sm={1} md={0.75} key={tip}>
                <Tooltip title={tip} arrow>
                  <Button
                    fullWidth
                    size="small"
                    onClick={action}
                    sx={{ ...iconBtn, height: 34 }}
                  >
                    {icon}
                  </Button>
                </Tooltip>
              </Grid>
            ))}

            {/* Spacer */}
            <Grid
              item
              xs={false}
              sm={false}
              md
              sx={{ display: { xs: "none", md: "block" } }}
            />

            {/* Stats */}
            <Grid item xs={6} sm={2} md={1.5}>
              <Box
                sx={{
                  height: 34,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  ...sq,
                  bgcolor: T.white,
                  color: T.primary,
                  border: `1.5px solid ${T.border}`,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  boxSizing: "border-box",
                }}
              >
                <Business sx={{ fontSize: 16, color: T.primary }} />
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: T.primary,
                    lineHeight: 1,
                  }}
                >
                  {totalCount} {totalCount !== 1 ? "Parties" : "Consignor"}
                </Typography>
              </Box>
            </Grid>

            {/* Add Button */}
            <Grid item xs={6} sm={2} md={1.5}>
              <Button
                fullWidth
                onClick={() => setAddModalOpen(true)}
                startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                sx={{ ...primaryBtn, height: 34 }}
              >
                Add Consignor
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* ── Table / Cards ── */}
        {isMobile ? (
          loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 10,
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: T.primary }} size={32} />
              <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
                Loading…
              </Typography>
            </Box>
          ) : paginatedParties.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                border: `1.5px dashed ${T.border}`,
                bgcolor: T.white,
                ...sq,
              }}
            >
              <Business sx={{ fontSize: 44, color: T.border, mb: 1 }} />
              <Typography
                sx={{
                  fontWeight: 700,
                  color: T.text,
                  mb: 0.5,
                  fontSize: "0.9rem",
                }}
              >
                No parties found
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: T.textSub, mb: 2 }}>
                {searchTerm
                  ? "Try a different search term"
                  : "Add your first party"}
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddModalOpen(true)}
                sx={{ ...primaryBtn, px: 2 }}
              >
                Add Consignor
              </Button>
            </Box>
          ) : (
            <>
              {paginatedParties.map((party) => (
                <MobilePartyCard
                  key={party.party_id}
                  party={party}
                  expanded={expandedCardId === party.party_id}
                  onToggle={() => handleCardToggle(party.party_id)}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                />
              ))}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, p) => {
                  setPage(p);
                  setExpandedCardId(null);
                }}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={paginationSx}
              />
            </>
          )
        ) : (
          <DesktopPartyTable
            parties={paginatedParties}
            loading={loading}
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
          />
        )}

        {/* ── Overlays ── */}
        <DeletePopover
          anchorEl={deleteAnchorEl}
          onClose={handleDeleteClose}
          onConfirm={handleDeleteConfirm}
        />
        <ViewPartyDialog
          open={viewOpen}
          onClose={() => setViewOpen(false)}
          party={selectedParty}
        />

        {/* ── Edit Dialog ── */}
        <Dialog
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              ...sq,
              border: `1.5px solid ${T.border}`,
              boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
              overflow: "hidden",
            },
          }}
        >
          <DialogHeader
            title="Edit Consignor"
            onClose={() => setEditModalOpen(false)}
          />
          <DialogContent sx={{ p: 2.5, bgcolor: T.white }}>
            <Stack spacing={1.75}>
              {[
                {
                  name: "name",
                  label: "Consignor Name",
                  type: "text",
                  rows: 0,
                },
                {
                  name: "contact_no",
                  label: "Contact No",
                  type: "tel",
                  rows: 0,
                },
                { name: "address", label: "Address", type: "text", rows: 2 },
                { name: "gst_no", label: "GST No", type: "text", rows: 0 },
                { name: "pan_no", label: "PAN No", type: "text", rows: 0 },
                {
                  name: "tds_percent",
                  label: "TDS %",
                  type: "number",
                  rows: 0,
                },
              ].map(({ name, label, type, rows }) => (
                <TextField
                  key={name}
                  name={name}
                  label={label}
                  type={type}
                  fullWidth
                  size="small"
                  value={editPartyData[name] || ""}
                  onChange={handleEditChange}
                  error={!!editErrors[name]} // ← directly from state
                  helperText={
                    name === "tds_percent"
                      ? "Default TDS % deducted by this party" // ← helper text for TDS
                      : editErrors[name] || ""
                  }
                  inputProps={
                    name === "tds_percent"
                      ? { min: 0, max: 100, step: 0.5 }
                      : undefined
                  }
                  InputProps={
                    name === "tds_percent"
                      ? {
                          endAdornment: (
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: "#6b7280",
                                pr: 0.5,
                              }}
                            >
                              %
                            </Typography>
                          ),
                        }
                      : undefined
                  }
                  {...(rows > 0 ? { multiline: true, rows } : {})}
                  sx={inputSx}
                />
              ))}
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              px: 2.5,
              py: 1.5,
              gap: 1,
              bgcolor: T.white,
              borderTop: `1px solid ${T.border}`,
            }}
          >
            <Button
              size="small"
              onClick={() => setEditModalOpen(false)}
              sx={{ ...ghostBtn, height: 32, px: 2 }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={() => handleSaveEdit(false)}
              sx={{ ...primaryBtn, height: 32, px: 2.5 }}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Add Consignor Dialog ── */}
        <Dialog
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              ...sq,
              border: `1.5px solid ${T.border}`,
              boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
              overflow: "hidden",
            },
          }}
        >
          <DialogHeader
            title="Add New Consignor"
            onClose={() => setAddModalOpen(false)}
          />
          <DialogContent sx={{ p: 0 }}>
            <AddParty
              setParties={setParties}
              setOpenAddModal={setAddModalOpen}
            />
          </DialogContent>
        </Dialog>
        {/* ✅ Edit Duplicate Confirmation Dialog */}
        <Dialog
          open={editDuplicateOpen}
          onClose={() => setEditDuplicateOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              ...sq,
              border: `1.5px solid ${T.border}`,
              boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
              overflow: "hidden",
            },
          }}
        >
          <DialogHeader
            title="Duplicate Name Found"
            onClose={() => setEditDuplicateOpen(false)}
          />
          <DialogContent sx={{ p: 2.5, bgcolor: T.white }}>
            <Typography sx={{ fontSize: "0.85rem", color: T.text, mb: 1 }}>
              Another party with the name{" "}
              <strong>"{editPartyData?.name}"</strong> already exists.
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: T.textSub }}>
              Do you want to update anyway?
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              px: 2.5,
              py: 1.5,
              gap: 1,
              bgcolor: T.white,
              borderTop: `1px solid ${T.border}`,
            }}
          >
            <Button
              size="small"
              onClick={() => setEditDuplicateOpen(false)}
              sx={{ ...ghostBtn, height: 32, px: 2 }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={() => handleSaveEdit(true)} // ✅ force = true
              sx={{ ...primaryBtn, height: 32, px: 2.5 }}
            >
              Update Anyway
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
